import { useMemo } from "react";
import dayjs from "dayjs";

import cx from "@hsl/core/utils/cx";

import { Translate } from "../../../../../hooks/useTranslation";
import { type UserDetails } from "../../../../../openapi";
import PencilIcon from "../../../../icons/Pencil";
import Select from "../../../../Select";
import { useSubscriptionContext } from "../../../Root";
import { useSelectDocumentContext } from "./context";

type DayOfWeek = UserDetails["dayOfWeek"];

type Props = {
    title?: string;
    description?: string;
    locale?: string;
    dayOfWeek?: DayOfWeek;
    onChange?(this: void, timing: DayOfWeek): void;
    className?: string;
    hideAdditionalText?: boolean;
};
const Weekdays = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
] as const;

const TimingSelector = (props: Props) => {
    const {
        className,
        locale = "en-GB",
        onChange,
        dayOfWeek,
        hideAdditionalText,
    } = props;
    const dropdownData = useMemo(
        () =>
            Weekdays.map((day, index) => ({
                key: day,
                label: dayjs()
                    .day(index + 1)
                    .toDate()
                    .toLocaleDateString(locale, { weekday: "long" }),
                value: day,
            })),
        [locale],
    );

    const widgets = [
        {
            title: <Translate>Weekly round-up</Translate>,
            node: (
                <div className="flex items-center gap-1">
                    <Select<Required<DayOfWeek>>
                        options={dropdownData}
                        onChange={(val) => {
                            if (onChange && !Array.isArray(val))
                                onChange(val as UserDetails["dayOfWeek"]);
                        }}
                        buttonContainerClassName="z-[1000]"
                        noArrow
                        value={dayOfWeek ?? "MONDAY"}
                        buttonClassName="text-sm"
                        optionClassName="text-sm border-b hover:font-normal px-4 py-3 last:border-b-0 cursor-pointer"
                        selectedOptionClassName="text-core-blue"
                        selectedOptionTick={false}
                    />
                    <PencilIcon width={10} height={10} />
                </div>
            ),
        },
        {
            title: <Translate>Monthly round-up</Translate>,
            node: (
                <p className="text-sm leading-5">
                    <Translate>
                        Your round-up email will be sent on the 15th day of each
                        month
                    </Translate>
                    .
                </p>
            ),
        },
        {
            title: <Translate>Quarterly round-up</Translate>,
            node: (
                <p className="text-sm leading-5">
                    <Translate>
                        Your round-up email will be sent one month after the end
                        of the quarter, e.g. the Q1 email will be sent on 30
                        April
                    </Translate>
                    .
                </p>
            ),
        },
    ];

    const content = (
        <ul className={cx("grid gap-4 sm:grid-cols-4 sm:gap-8", className)}>
            {widgets.map((widget, index) => (
                <li
                    key={`timing-widget-${index}`}
                    className="max-sm:before:bg-core-green bg-white before:absolute before:bottom-0 before:left-0 before:top-0 before:w-[25%] max-sm:relative max-sm:grid max-sm:grid-cols-[25%_1fr] max-sm:items-center max-sm:before:content-['']"
                >
                    <h5 className="bg-core-green py-6 text-center text-sm font-medium leading-none text-white max-sm:relative max-sm:my-auto max-sm:break-words max-sm:px-2">
                        {widget.title}
                    </h5>
                    <div className="px-3 max-sm:py-4 sm:pb-7 sm:pt-4">
                        {widget.node}
                    </div>
                </li>
            ))}
        </ul>
    );

    if (hideAdditionalText) {
        return content;
    } else {
        return (
            <>
                <h3 className="mb-1 mt-10 text-lg font-medium">
                    <Translate>
                        {props.title ?? "Timing of round-up emails"}
                    </Translate>
                </h3>
                <p className="mb-6">
                    {props.description ?? (
                        <>
                            <Translate>
                                You can adjust the timing of your round-up
                                emails below
                            </Translate>
                            .
                        </>
                    )}
                </p>
                {content}
            </>
        );
    }
};

export const TimingSelectorWithContext = (props: Omit<Props, "onChange">) => {
    const { updateDayOfWeek, stepData } = useSelectDocumentContext();

    const {
        config: { locale },
    } = useSubscriptionContext();
    return (
        <TimingSelector
            {...props}
            onChange={updateDayOfWeek}
            locale={locale.replace("_", "-")}
            dayOfWeek={stepData.data?.dayOfWeek}
        />
    );
};

export default TimingSelector;
